import React, {CSSProperties, KeyboardEvent, ReactElement, useCallback, useRef, useState} from 'react';
import './MultiSelect.less';
import IconButton from "./IconButton/IconButton";
import Icons from "./IconButton/Icons";
import {Tooltip} from "@mui/material";

/**
 * Simple multi-select checkbox component.
 *
 * Props include a list of all possible options and a list of selected options, along with layout options.
 *
 * Implements roving tabIndex: one tab stop with arrow keys to navigate between options,
 * Space/Enter to toggle.
 */
function MultiSelect(props: MultiSelectProps) {
    const [hoverIndex, setHoverIndex] = useState<number>(-1);
    const [focusedIndex, setFocusedIndex] = useState<number>(0);
    const optionDivsRef = useRef<(HTMLDivElement | null)[]>([]);

    const {selected, onClick: onClickProp, labels, available, disabled, alwaysAvailable} = props;

    /**
     * Handle option checkbox clicks.  If the option is in the selected array, remove it; otherwise add it.
     *
     * @param {string} option - selected option
     * @param {number} index
     */
    const onClick = useCallback((option: string, index: number) => {
        const selectedCopy = [...selected];

        const selectedIndex = selectedCopy.indexOf(option);

        selectedIndex === -1 ? selectedCopy.push(option) : selectedCopy.splice(selectedIndex, 1);

        onClickProp(selectedCopy || [], index);

    }, [selected, onClickProp]);

    const onMouseOver = (hoverIndex: number) => {
        setHoverIndex(hoverIndex);
    };

    const onMouseOut = () => {
        setHoverIndex(-1);
    };

    const getLabel = useCallback((option: string | ReactElement, index: number): string => {
        return typeof option !== 'string' ? labels.at(index) : option;
    }, [labels]);

    const isOptionDisabled = useCallback((index: number): boolean => {
        if (!disabled || index >= available.length) return false;

        return !alwaysAvailable?.includes(getLabel(available[index], index));
    }, [available, disabled, alwaysAvailable, getLabel]);

    // The tab stop is never on a disabled option: its button cannot take focus (OWA-1777).
    const tabStop = isOptionDisabled(focusedIndex)
                    ? available.findIndex((_, index) => !isOptionDisabled(index))
                    : focusedIndex;

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        const count = available.length;
        if (count === 0) return;

        const moveFocus = (step: 1 | -1) => {
            e.preventDefault();
            for (let distance = 1; distance < count; distance++) {
                const nextIdx = (tabStop + step * distance + count * distance) % count;
                if (isOptionDisabled(nextIdx)) continue;

                setFocusedIndex(nextIdx);
                optionDivsRef.current[nextIdx]?.querySelector<HTMLButtonElement>('button')?.focus();
                return;
            }
        };

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') moveFocus(1);
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') moveFocus(-1);
        else if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            if (tabStop === -1 || isOptionDisabled(tabStop)) return;

            onClick(getLabel(available[tabStop], tabStop), tabStop);
        }
    }, [tabStop, available, getLabel, isOptionDisabled, onClick]);

    const wrap = props.wrap ? 'wrap' : 'nowrap';

    return (
        <div className="MultiSelect"
             role="group"
             aria-label={props.ariaLabel}
             style={{flexDirection: props.orientation, flexWrap: wrap}}
             onKeyDown={handleKeyDown}
        >
            {
                props.available.map((option, index) => {
                    const usesElement = typeof (option) !== 'string';
                    const label = usesElement ? props.labels.at(index) : option;

                    // Is this option selected?
                    const selected = props.selected.includes(label);
                    const icon = selected ? Icons.CHECKED : Icons.UNCHECKED;
                    const data = props.data ? props.data.at(index) : null;
                    const bold = props.boldHandler ? props.boldHandler(index, data) ? 'MultiSelect-Bold' : '' : '';
                    const required = props.required ? 'required' : '';
                    const hint = (props.hints && index === hoverIndex) ? props.hints.at(index) : null;

                    let style: CSSProperties = {};
                    if (usesElement) style.marginTop = '0px';

                    return (
                        <Tooltip key={`MultiSelect_${props.id}_${index}`}
                                 title={hint}
                        >
                            <div className={`MultiSelect-Option ${bold} ${required}`}
                                 ref={el => { optionDivsRef.current[index] = el; }}
                                 onMouseOver={() => onMouseOver(index)}
                                 onFocus={() => { onMouseOver(index); setFocusedIndex(index); }}
                                 onMouseOut={onMouseOut}
                                 onBlur={onMouseOut}
                            >
                                <IconButton id={`MultiSelect-Option-${typeof (label) === 'string' ? label : index}`}
                                            tabIndex={index === tabStop ? 0 : -1}
                                            type={icon}
                                            disabled={isOptionDisabled(index)}
                                            onClick={() => onClick(label, index)}
                                            style={style}
                                />
                                <div className="MultiSelect-Label"
                                     onClick={(isOptionDisabled(index) || usesElement) ? undefined : () => onClick(label, index)}
                                >
                                    {option}
                                </div>
                            </div>
                        </Tooltip>
                    );
                })
            }
        </div>
    );
}

export type MultiSelectProps = {
    id: string;
    alwaysAvailable?: (string | ReactElement)[];
    ariaLabel?: string;
    available: (string | ReactElement)[];
    boldHandler?: (index: number, data: any) => boolean;
    data?: object[];
    disabled?: boolean;
    hints?: string[];
    labels?: string[];
    onClick: (label: string[], index?: number) => void;
    orientation: 'row' | 'column';
    required?: boolean;
    selected: string[];
    wrap?: boolean;
}

export default MultiSelect;
